import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ClickAwayListener, Fade } from "@mui/material";
import { StyledPopper } from "@hcl-commerce-store-sdk/react-component";
import { StyledBox, StyledButton, StyledIconButton, StyledLink, StyledTypography } from "../../../../styled-mui";
import { SvgChevronDown } from "../../../svg/chevronDown";
import { SvgClose } from "../../../svg/close";
import { EXTERNAL_LINK, LINK_USAGE, NAVIGATION_IDENTIFIERS } from "../../../../constants/marketing";
import AsyncCall from "../../../../_foundation/gtm/async.service";
import { CLICK } from "../../../../_foundation/constants/gtm";

interface Props {
  /* index and popperRef used for logic to open every hover menu under first menu item */
  index: number;
  popperRef?: React.RefObject<HTMLButtonElement> | null;
  /* */
  isMenu: boolean;
  menuItem: Record<string, any> | null;
  selectedMenuItem?: string | null;
  setSelectedMenuItem: React.Dispatch<React.SetStateAction<string | null>>;
  menuLinksData: Record<string, any>;
  OnHoverUI?: React.ComponentType<any> | null;
  obfuscate?: boolean;
  pageParams?: Record<string, any> | null;
  categories?: Record<string, any>[];
  pathname?: string;
}

const DesktopMenuItem: React.FC<Props> = ({
  index,
  popperRef,
  isMenu,
  menuItem = null,
  selectedMenuItem = null,
  setSelectedMenuItem,
  menuLinksData,
  OnHoverUI = null,
  obfuscate = false,
  pageParams = null,
  categories = [],
  pathname = "",
}) => {
  const [scrollDir, setScrollDir] = useState("up");
  const navigate = useNavigate();

  useEffect(() => {
    const threshold = 0;
    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateScrollDir = () => {
      const scrollY = window.scrollY;
      if (scrollY > 150) {
        if (Math.abs(scrollY - lastScrollY) < threshold) {
          ticking = false;
          return;
        }
        setScrollDir(scrollY > lastScrollY ? "down" : "up");
        lastScrollY = scrollY > 0 ? scrollY : 0;
      }
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollDir);
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll);
    if (scrollDir === "down") {
      setSelectedMenuItem(null);
    }

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollDir]);

  useEffect(() => {
    let styleTag = document.getElementById("menu-overlay-style") as HTMLStyleElement;

    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = "menu-overlay-style";
      document.head.appendChild(styleTag);
    }

    if (selectedMenuItem) {
      styleTag.innerHTML = `
      body.menu-overlay-active::after {
        content: "";
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.35);
        z-index: 9;
        pointer-events: none;
      }
    `;

      document.body.classList.add("menu-overlay-active");
    } else {
      document.body.classList.remove("menu-overlay-active");
    }

    return () => {
      document.body.classList.remove("menu-overlay-active");
    };
  }, [selectedMenuItem]);

  const closeMenu = (event) => {
    const target = event.target;
    if (popperRef?.current && (popperRef.current.contains(target) || popperRef.current === target)) {
      return;
    }
    setSelectedMenuItem(null);
  };

  const onMouseOver = () => {
    setSelectedMenuItem(menuItem?.seo?.identifier || null);
  };

  const onMouseOut = () => {
    if (isMenu) return;
    setSelectedMenuItem(null);
  };

  const handleClose = (event) => {
    event.preventDefault();
    setSelectedMenuItem(null);
  };

  const sendMenuClickGTMEvent = (gtmData: Record<string, string> = {}) => {
    AsyncCall.sendClick(CLICK.NAV, {
      click_section: "menu",
      nav_level_1: menuItem?.seo?.label,
      nav_level_2: "",
      nav_level_3: "",
      nav_type: "cta",
      btn_label: menuItem?.seo?.label || "",
      click_destination: "",
      ...gtmData,
    });
  };

  return (
    <>
      <StyledBox
        p="0px 8px"
        className={`${
          selectedMenuItem === menuItem?.seo?.identifier ? "expanded-menu expanded-menu-hover" : "expanded-menu"
        } ${menuItem?.usage === LINK_USAGE.EVENT ? "expanded-menu-hover-blue" : ""}`}>
        {isMenu ? (
          <StyledButton
            testId={`header-product`}
            variant="text"
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
            onClick={() => sendMenuClickGTMEvent()}
            color="black"
            ref={index === 0 ? popperRef : null}
            sx={{ height: "56px", minWidth: "max-content !important" }}
            endIcon={
              menuItem?.usage === LINK_USAGE.STANDARD &&
              menuItem?.seo?.identifier === NAVIGATION_IDENTIFIERS.PRODUCTS ? (
                <SvgChevronDown />
              ) : (
                <></>
              )
            }>
            <StyledTypography className="expanded-menu-item" variant="body4" data-testid="menubar-menuitem-button">
              {menuItem?.seo?.label || ""}
            </StyledTypography>
          </StyledButton>
        ) : obfuscate ? (
          <span
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
            role="link"
            tabIndex={0}
            aria-label={menuItem?.seo?.label}
            data-testid={`header-${menuItem?.seo?.label}`}
            style={{ cursor: "pointer", display: "inline-block" }}
            onClick={() => {
              sendMenuClickGTMEvent({ nav_type: "promo_block", click_destination: menuItem?.seo?.url || "" });
              if (menuItem?.seo?.target === "_blank") {
                window.open(menuItem?.seo?.url, "_blank");
              } else if (menuItem?.seo?.url) {
                navigate(menuItem.seo.url);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                sendMenuClickGTMEvent({ nav_type: "promo_block", click_destination: menuItem?.seo?.url || "" });
                if (menuItem?.seo?.target === "_blank") {
                  window.open(menuItem?.seo?.url, "_blank");
                } else if (menuItem?.seo?.url) {
                  navigate(menuItem.seo.url);
                }
              }
            }}>
            <StyledTypography
              color={
                menuItem?.usage === LINK_USAGE.PROMO
                  ? "primary"
                  : menuItem?.usage === LINK_USAGE.EVENT
                  ? "secondary"
                  : "black"
              }
              className="expanded-menu-item"
              variant={menuItem?.usage === LINK_USAGE.STANDARD ? "body4" : "body3"}
              data-testid="menubar-menuitem-button"
              sx={{ display: "flex", alignItems: "center", height: "56px", whiteSpace: "nowrap" }}>
              {menuItem?.seo?.label || ""}
            </StyledTypography>
          </span>
        ) : (
          <StyledLink
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
            testId={`header-${menuItem?.seo?.label}`}
            key={"Link_" + menuItem?.seo?.label}
            to={menuItem?.seo?.url}
            target={menuItem?.seo?.target || "_self"}
            href={menuItem?.seo?.type === EXTERNAL_LINK ? menuItem?.seo?.url : null}
            onClick={() =>
              sendMenuClickGTMEvent({
                nav_type: "promo_block",
                click_destination: menuItem?.seo?.url || "",
              })
            }>
            <StyledTypography
              color={
                menuItem?.usage === LINK_USAGE.PROMO
                  ? "primary"
                  : menuItem?.usage === LINK_USAGE.EVENT
                  ? "secondary"
                  : "black"
              }
              className="expanded-menu-item"
              variant={menuItem?.usage === LINK_USAGE.STANDARD ? "body4" : "body3"}
              data-testid="menubar-menuitem-button"
              sx={{ display: "flex", alignItems: "center", height: "56px", whiteSpace: "nowrap" }}>
              {menuItem?.seo?.label || ""}
            </StyledTypography>
          </StyledLink>
        )}
      </StyledBox>

      {isMenu && (
        <ClickAwayListener onClickAway={closeMenu}>
          <StyledPopper
            onMouseLeave={() => setSelectedMenuItem(null)}
            id="menu-popper-produits"
            data-testid="menu-popper-produits"
            open={selectedMenuItem === menuItem?.seo?.identifier}
            anchorEl={popperRef?.current}
            placement="bottom-start"
            disablePortal
            className="popper-container"
            modifiers={[
              {
                name: "flip",
                enabled: false,
              },
              {
                name: "preventOverflow",
                enabled: false,
              },
              {
                name: "hide",
                enabled: false,
              },
            ]}
            transition>
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <StyledBox>
                  {/* destruct/use closeHeaderMenu and menuLinksData prop on your hover component and call it to close menu */}
                  {OnHoverUI && (
                    <OnHoverUI
                      closeHeaderMenu={() => setSelectedMenuItem(null)}
                      menuLinksData={menuLinksData}
                      pageParams={pageParams}
                      categories={categories}
                      pathname={pathname}
                    />
                  )}
                  <StyledIconButton onClick={handleClose} className="expanded-menu-svg-close">
                    <SvgClose color="black" />
                  </StyledIconButton>
                </StyledBox>
              </Fade>
            )}
          </StyledPopper>
        </ClickAwayListener>
      )}
    </>
  );
};

export default DesktopMenuItem;
